import {Segment} from 'platform/components';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {
  PayPerUsePeriod,
  useGetPayPerUseLeaderboardQuery,
  useGetPayPerUseMetricsQuery,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';

import {PayPerUseKpiHeader} from './components/PayPerUseKpiHeader';
import {PayPerUseLeaderboardTable} from './components/PayPerUseLeaderboardTable';
import {PayPerUseOperationCards} from './components/PayPerUseOperationCards';
import {
  DEFAULT_PAY_PER_USE_PERIOD,
  PAY_PER_USE_PERIOD_LABEL,
  PAY_PER_USE_PERIODS,
} from './constants/PAY_PER_USE_PERIODS';
import {getOperationTypeColumns} from './utils/getOperationTypeColumns';
import {getVisiblePreviousTotalCount} from './utils/getVisiblePreviousTotalCount';
import {getVisibleTotalCount} from './utils/getVisibleTotalCount';
import {getVisibleUserTotal} from './utils/getVisibleUserTotal';

export function PayPerUseMetrics() {
  const [period, setPeriod] = useState<PayPerUsePeriod>(DEFAULT_PAY_PER_USE_PERIOD);

  const {
    data: metricsData,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
  } = useGetPayPerUseMetricsQuery({period});
  const {
    data: leaderboardData,
    isLoading: isLeaderboardLoading,
    isError: isLeaderboardError,
  } = useGetPayPerUseLeaderboardQuery({period});

  const periodOptions = PAY_PER_USE_PERIODS.map((periodValue) => ({
    value: periodValue,
    label: i18n.t(PAY_PER_USE_PERIOD_LABEL[periodValue]),
  }));

  // Subtract hidden operations from the backend-provided totals so the headline
  // total, "% of total", and the leaderboard totals reflect only visible ops.
  const visibleMetrics = isNotNil(metricsData)
    ? {
        ...metricsData,
        totalCount: getVisibleTotalCount(metricsData),
        previousTotalCount: getVisiblePreviousTotalCount(metricsData),
      }
    : undefined;
  const visibleUsers = leaderboardData?.users.map((user) => ({
    ...user,
    total: getVisibleUserTotal(user),
  }));

  return (
    <SettingsTemplate
      data-testid={testIds.settings.payPerUseMetrics('template')}
      header={{
        title: i18n.t('page.homepage.payPerUse.title'),
      }}
      isLoading={isMetricsLoading || isLeaderboardLoading}
      isError={isMetricsError || isLeaderboardError}
    >
      <Show
        when={
          isNotNil(visibleMetrics) && isNotNil(visibleUsers) ? {visibleMetrics, visibleUsers} : null
        }
      >
        {(payPerUse) => (
          <VStack spacing={6}>
            <HStack justify="space-between" align="center" spacing={4}>
              <Text size="small" color="secondary">
                {i18n.t('page.payPerUseMetrics.description')}
              </Text>
              <HStack align="center" spacing={3}>
                <Text size="small" color="secondary">
                  {i18n.t('entity.user.labels.period')}
                </Text>
                <Segment<PayPerUsePeriod>
                  value={period}
                  options={periodOptions}
                  onChange={setPeriod}
                  data-testid={testIds.settings.payPerUseMetrics('period')}
                />
              </HStack>
            </HStack>

            <PayPerUseKpiHeader
              metrics={payPerUse.visibleMetrics}
              data-testid={testIds.settings.payPerUseMetrics('kpi')}
            />

            <VStack spacing={3}>
              <Heading size={4}>{i18n.t('page.payPerUseMetrics.operationCards.title')}</Heading>
              <PayPerUseOperationCards
                operationTypes={payPerUse.visibleMetrics.operationTypes}
                totalCount={payPerUse.visibleMetrics.totalCount}
                data-testid={testIds.settings.payPerUseMetrics('operationCards')}
              />
            </VStack>

            <VStack spacing={3}>
              <Heading size={4}>{i18n.t('page.payPerUseMetrics.leaderboard.title')}</Heading>
              <PayPerUseLeaderboardTable
                // Reset the table's local state (sort, search, page, hidden columns)
                // when the period changes — the operation-type columns differ per
                // period, so a sort on a now-absent column would otherwise detach.
                key={period}
                period={period}
                users={payPerUse.visibleUsers}
                operationColumns={getOperationTypeColumns(payPerUse.visibleMetrics.operationTypes)}
                data-testid={testIds.settings.payPerUseMetrics('leaderboard')}
              />
            </VStack>
          </VStack>
        )}
      </Show>
    </SettingsTemplate>
  );
}
